
import React, { useState, useEffect } from 'react';
import { Bell, Settings, Smartphone, Chrome } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const NotificationPermissionPrompt: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    isSupported,
    permission,
    isSubscribed,
    loading,
    requestPermission,
    subscribeUser
  } = usePushNotifications();
  
  const [deviceInfo, setDeviceInfo] = useState<{
    isIOS: boolean;
    isAndroid: boolean;
    isStandalone: boolean;
    browser: string;
  }>({
    isIOS: false,
    isAndroid: false,
    isStandalone: false,
    browser: 'unknown'
  });

  useEffect(() => {
    // Delayed device detection to prevent iOS Safari crashes
    const detectDevice = () => {
      try {
        const userAgent = (navigator.userAgent || '').toLowerCase();
        const isIOS = /ipad|iphone|ipod/.test(userAgent);
        const isAndroid = /android/.test(userAgent);
        
        // SAFE standalone detection - wrapped in try-catch for iOS Safari
        let isStandalone = false;
        try {
          // Try legacy standalone first (more reliable)
          isStandalone = (window.navigator as any).standalone === true;
          
          // Then try matchMedia with protection
          if (!isStandalone) {
            isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches ?? false;
          }
        } catch {
          // matchMedia failed - use legacy only
          isStandalone = (window.navigator as any).standalone === true;
        }
        
        let browser = 'unknown';
        if (userAgent.includes('chrome')) browser = 'chrome';
        else if (userAgent.includes('safari')) browser = 'safari';
        else if (userAgent.includes('firefox')) browser = 'firefox';
        
        setDeviceInfo({ isIOS, isAndroid, isStandalone, browser });
      } catch (error) {
        console.warn('Device detection failed:', error);
      }
    };
    
    // Small delay to let iOS Safari stabilize
    const timeoutId = setTimeout(detectDevice, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  // Don't show if user is not logged in or push is not supported
  if (!user || !isSupported) {
    return null;
  }

  // Don't show if already subscribed or permission denied
  if (isSubscribed || permission === 'denied') {
    return null;
  }

  // Only show for default permission state (user hasn't been asked yet)
  if (permission !== 'default') {
    return null;
  }

  const handleEnable = async () => {
    const granted = await requestPermission();
    if (granted) {
      await subscribeUser();
    }
  };

  const getInstructions = () => {
    if (deviceInfo.isIOS && !deviceInfo.isStandalone) {
      return (
        <Alert className="mb-4">
          <Smartphone className="h-4 w-4" />
          <AlertDescription>
            <strong>iOS users:</strong> For reliable notifications, please add LuvALL to your home screen first. 
            Tap the share button <span className="font-mono">⤴</span> and select "Add to Home Screen".
          </AlertDescription>
        </Alert>
      );
    }
    
    if (deviceInfo.isAndroid && deviceInfo.browser === 'chrome') {
      return (
        <Alert className="mb-4">
          <Chrome className="h-4 w-4" />
          <AlertDescription>
            <strong>Android Chrome:</strong> Make sure Chrome notifications are enabled in your device settings for the best experience.
          </AlertDescription>
        </Alert>
      );
    }
    
    return null;
  };

  return (
    <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5 mx-4">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Enable Notifications</CardTitle>
        </div>
        <CardDescription>
          Stay updated with match results, tournament updates, and group activities. 
          Get instant notifications for important LuvALL events.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {getInstructions()}
        <div className="flex gap-3">
          <Button 
            onClick={handleEnable}
            disabled={loading}
            className="flex-1"
          >
            {loading ? 'Enabling...' : 'Enable Notifications'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/settings/notifications')}
            className="flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
